import Loading from '@app/components/loading.tsx';
import { CurrencyContext } from '@app/components/price/use-currency.ts';
import { getFragmentData } from '@app/graphql/types';
import { BannerPlatform, MenuProductItemFragment } from '@app/graphql/types/graphql.ts';
import ApplicationErrorView from '@app/module/error/application-error-view.tsx';
import { BranchFragment } from '@app/page/online-order/model/branch-fragment.ts';
import { MenuFragment, MenuProductFragment } from '@app/page/online-order/model/menu-product-fragment.ts';
import { MenuSectionFragment } from '@app/page/online-order/model/menu-section-fragment.ts';
import { OnlineOrderBranchConfigurationFragment } from '@app/page/online-order/model/online-order-branch-configuration.ts';
import AlertBanner from '@app/page/online-order/product-picker/component/alert-banner.tsx';
import BusinessCard from '@app/page/online-order/product-picker/component/business-card.tsx';
import CartBar from '@app/page/online-order/product-picker/component/cart-bar.tsx';
import CriticalError from '@app/page/online-order/product-picker/component/critical-error.tsx';
import FunctionalBar from '@app/page/online-order/product-picker/component/functional-bar.tsx';
import PageNotFound from '@app/page/online-order/product-picker/component/page-not-found.tsx';
import ProductPickerList from '@app/page/online-order/product-picker/component/product-picker-list.tsx';
import UnavailableOnlineOrder from '@app/page/online-order/product-picker/component/unavailable-online-order.tsx';
import { useProductPickerQuery } from '@app/page/online-order/product-picker/logic/use-product-picker-page-query.ts';
import { useProductPicker } from '@app/page/online-order/product-picker/logic/use-product-picker.ts';
import { SortType } from '@app/page/online-order/product-picker/utils/sort-type.ts';
import { analytics } from '@app/utils/analytics.ts';
import { loadTemplate, normalizeTemplateEnum } from '@app/utils/template-loader.ts';
import { useTranslate, useTolgee } from '@tolgee/react';
import { logEvent, setUserProperties } from 'firebase/analytics';
import { getInitialLanguage, setAutoLanguage } from '@app/utils/language-detection';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TopBanner from '../banner/top-banner/top-banner';
import MinimumPriceDialog from './component/minimum-price-dialog';
import useCreateOnlineOrderV2 from './logic/use-create-online-order-v2';
import { useMinimumPriceDialogController } from './logic/use-minimum-price-dialog-controller';
import NewsBanner from '../banner/news-banner/news-banner';
import CartSideBar from './component/cart-side-bar';

const ProductPickerPage = () => {
  const [params] = useSearchParams();
  const branchID = params.get('branchID');

  const { t } = useTranslate();
  const tolgee = useTolgee(['language']);

  const { data, loading, error: loadingError } = useProductPickerQuery(branchID);

  const [products, setProducts] = useState<readonly MenuProductItemFragment[]>([]);
  const [sortType, setSortType] = useState<SortType>(SortType.NATURAL);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const { isOpen, closeDialog, openDialog } = useMinimumPriceDialogController();

  const setupStore = useProductPicker((state) => state.setup);

  const branch = getFragmentData(BranchFragment, data?.storefront_branch);
  const configuration = getFragmentData(
    OnlineOrderBranchConfigurationFragment,
    data?.storefront_onlineOrderBranchConfiguration
  );
  const menu = getFragmentData(MenuFragment, configuration?.menu);
  const sections = getFragmentData(MenuSectionFragment, menu?.sections) ?? [];

  const { createOnlineOrder, creatingOnlineOrder, creatingError } = useCreateOnlineOrderV2({
    branchId: branchID ?? '',
    minimumPrice: configuration?.minimumOrderAmountForDelivery ?? 0,
    openDialog,
  });

  const error = loadingError ?? creatingError;

  const alternateOrderUrl = branch?.company.hubPage?.alternateOrderUrl;

  // Check for alternate order URL and redirect if available
  useEffect(() => {
    if (alternateOrderUrl) {
      window.location.href = alternateOrderUrl;
    }
  }, [alternateOrderUrl]);

  useEffect(() => {
    if (branch) {
      if (branch.company.id) {
        setUserProperties(analytics, { companyId: branch.company.id, branchId: branchID });
      }
      logEvent(analytics, 'online_order_start', {
        companyId: branch.company.id,
        branchID: branchID,
      });
    }
  }, [branch, branchID]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const products = getFragmentData(MenuProductFragment, menu?.menuProducts) ?? [];
    setProducts(products);
  }, [data, menu?.menuProducts]);

  useEffect(() => {
    if (branch) {
      setupStore(branch.id);
    }
  }, [branch, setupStore]);

  // Smart language detection with priority system
  useEffect(() => {
    if (!branch?.company.settings.customerDefaultLanguage) {
      return;
    }

    const initialLanguage = getInitialLanguage(branch.company.settings.customerDefaultLanguage);
    setAutoLanguage(initialLanguage, tolgee.changeLanguage);
  }, [branch?.company.settings.customerDefaultLanguage, tolgee.changeLanguage]);

  useEffect(() => {
    if (configuration?.template) {
      setIsLoadingTemplate(true);
      const templateId = normalizeTemplateEnum(configuration.template);
      loadTemplate(templateId)
        .then(() => {
          setIsLoadingTemplate(false);
        })
        .catch((error) => {
          console.error('Failed to load template from backend configuration:', error);
          setIsLoadingTemplate(false);
        });
    }
  }, [configuration?.template]);

  // Check
  if (loading || isLoadingTemplate) return <Loading />;

  // Keep the menu hidden while the browser navigates to the alternate order URL
  if (alternateOrderUrl) return <Loading />;

  if (loadingError) {
    return <CriticalError />;
  }

  if (!data) {
    return <PageNotFound />;
  }

  if (!branch) {
    return <PageNotFound />;
  }

  if (!configuration || !menu) {
    return <PageNotFound />;
  }

  if (!configuration.deliveryEnable && !configuration.pickupEnable) {
    return <UnavailableOnlineOrder website={branch.website ?? ''} />;
  }

  const isClosingNow = !data.storefront_isOnlineOrderAvailable || data.storefront_isHolidayNow;

  // Handler
  const createOrderHandler = () => {
    createOnlineOrder();
  };

  const searchProduct = (searchString: string) => {
    const products = getFragmentData(MenuProductFragment, menu.menuProducts);

    const newProducts = products.filter((item) => {
      const searchTerm = searchString.toLowerCase().trim();
      const titleMatch = item.title.toLowerCase().includes(searchTerm);
      const codeMatch = item.code.toLowerCase().includes(searchTerm);

      return titleMatch || codeMatch;
    });

    setProducts(newProducts);
  };

  return (
    <CurrencyContext.Provider value={branch.company.settings.currency}>
      <div className="online-order-page__banners">
        <TopBanner branchID={branch.id} platform={BannerPlatform.OnlineOrder} />

        {isClosingNow && (
          <div className="online-order-page__alerts border-t border-white">
            <AlertBanner
              description={t(
                'online-order.restaurant-close.alert',
                'The restaurant is not accepting new orders for today.'
              )}
            />
          </div>
        )}

        <ApplicationErrorView error={error} />
      </div>

      <div className="online-order-page">
        <div className="online-order-page__container">
          <div className="online-order-page__main-column">
            <BusinessCard branch={branch} configuration={configuration} callFromPickerPage={true} />

            <NewsBanner branchID={branch.id} platform={BannerPlatform.OnlineOrder} />
            <div className="online-order-page__products card">
              <div className="online-order-page__functional-bar">
                <FunctionalBar
                  searchProduct={searchProduct}
                  sections={sections}
                  products={products}
                  setSortType={(sortType: SortType) => {
                    setSortType(sortType);
                  }}
                  sortType={sortType}
                />
              </div>

              <ProductPickerList
                branch={branch}
                timezone={branch.company.settings.timezone}
                sections={sections}
                products={products}
                sortType={sortType}
              />
            </div>
          </div>
          <div className="online-order-page__sidebar-column">
            <CartSideBar
              menu={menu}
              onCreate={createOrderHandler}
              disableOrderButton={creatingOnlineOrder || isClosingNow}
            />
          </div>
          <div className="online-order-page__mobile-cart">
            <CartBar menu={menu} onCreate={createOrderHandler} disableOrderButton={creatingOnlineOrder || isClosingNow} />
          </div>
          <MinimumPriceDialog closeDialog={closeDialog} isOpen={isOpen} />
        </div>
      </div>
    </CurrencyContext.Provider>
  );
};

export function BlockPage({ isClosingNow, errorMessage }: { isClosingNow: boolean; errorMessage: string }) {
  if (isClosingNow) {
    return (
      <>
        <AlertBanner description={errorMessage} />
        <div className="fixed left-0 top-0 z-40 h-full w-full bg-gray-900/50"></div>
      </>
    );
  } else {
    return <></>;
  }
}

export default ProductPickerPage;
